import { Badge, Card, DataTable, EmptyState, LegacyCard, Link, Page, SkeletonThumbnail, Tabs, Text } from '@shopify/polaris';
import Pagination from 'components/pagination';
import SimpleFilter from 'components/simpleFilter';
import SkeletonLoading from 'components/skeletonPageLoading';
import dateandtime from 'date-and-time';
import helpers from 'helpers/index';
import emptyIMG from 'media/images/empty.png';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@shopify/polaris-icons';
import { TypedTransactions, allowed_transaction_category } from 'interface/transaction.model';
import __, { ___ } from 'languages/index';
import { useGetTransactions } from 'queries/transaction.query';
import { useGetAdminWallet, useGetMyOneWallet, useGetMyTransaction } from 'queries/user_wallet.query';
import { useGetWallets } from 'queries/wallet.query';
import { Helmet } from 'react-helmet-async';
import { useAuth } from 'context/AuthContext';
import __helpers from 'helpers/index';
import AgencyTransactionView from './agency.transaction.view';

export default function AgencyTransaction() {
  const { data: walletEntities } = useGetWallets({ limit: 30 });
  const [walletTypeList, setWalletTypeList] = useState<any>([]);
  const [walletSelected, setWalletSelected] = useState(0);

  useEffect(() => {
    if (walletEntities?.body) {
      setWalletTypeList(walletEntities?.body);
    }
  }, [walletEntities]);

  const history = useNavigate();

  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let { search } = useLocation();
  let StringQuery = helpers.ExtractUrl(search) || false;

  const initialQuery = useMemo(() => {
    return {
      query: '',
      page: 1,
      limit: 50,
      sort: 'createdAt:desc',
      transaction_category: '',
    };
  }, []);

  const [mainQuery, setMainQuery] = useState({
    ...initialQuery,
    ...StringQuery,
  });
  const [tabs, setTabs] = useState([]);

  const { data, refetch: getEntities, isFetching: loading } = useGetMyTransaction(tabs[walletSelected]?.wallet_unit || 'cash', mainQuery);
  const { refetch: getWalletData, data: walletData } = useGetMyOneWallet(tabs[walletSelected]?.wallet_unit || 'cash');
  const [entities, setEntities] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (data) {
      let { body, totalItems } = data;
      setEntities(body);
      setTotalItems(totalItems);
    }
  }, [data]);

  const [showModal, setShowModal] = useState(false);

  /**
   * Change page number
   */
  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  const getMainQuery = useCallback(async (mainQuery: any) => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) history('/agency/transaction' + buildURLSearch);
    getEntities();
  }, []);
  const getMainQueryDebound = useMemo(() => helpers.debounce((_value) => getMainQuery?.call(this, _value), 500), []);

  useEffect(() => {
    getMainQueryDebound(mainQuery);
  }, [mainQuery]);

  /**
   * I do not know ...
   * Bug: In React, do NOT remove this code
   */
  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  /**
   * Load current content
   */
  const reloadContent = useCallback(() => {
    getEntities();
    setShowModal(false);
  }, [mainQuery]);

  /** showTransactionDetail */
  const [showTransactionDetail, setShowTransactionDetail] = useState<string>('');

  const emptyData = (
    <EmptyState heading="Không có giao dịch nào!" image={emptyIMG}>
      <p>Oh, Không có gì ở đây. Hãy thử bỏ bộ lọc hoặc tạo mới.</p>
    </EmptyState>
  );

  let totalMoney = 0;
  let totalID = 0;

  const renderItem = (transactionData: TypedTransactions) => {
    if (!transactionData) return;
    const {
      transaction_id,
      amount,
      transaction_note,
      transaction_type,
      transaction_category,
      transaction_reference,
      transaction_hash,
      createdAt,
      wallet_receiver,
      creator_to_user,
    } = transactionData;

    totalMoney += Number(amount);
    totalID++;

    return [
      <Text as="span">{String(transaction_id)}</Text>,
      helpers.formatNumber(amount, ','),
      transaction_note || <Badge></Badge>,
      <Badge tone={transaction_type === 'credit' ? 'success' : 'critical'}>{__('transaction_type_' + transaction_type)}</Badge>,
      <Badge tone="info">{__('transaction_category_' + transaction_category)}</Badge>,
      <Link onClick={() => setShowTransactionDetail(transaction_hash)}>{__helpers.trimContentString(transaction_hash)}</Link>,
      <div key={transaction_id}>{createdAt ? dateandtime.format(new Date(Number(createdAt)), 'HH:mm:ss DD-MM-YYYY') : '-'}</div>,
    ];
  };

  const PagesList =
    entities && entities.length > 0 ? (
      <>
        <DataTable
          truncate
          sortable={[false, false, false, false]}
          defaultSortDirection="descending"
          initialSortColumnIndex={1}
          onSort={null}
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
          headings={['ID', 'Số tiền', 'G.chú', 'Phương thức', 'Loại', 'Mã giao dịch', 'Thời gian']}
          rows={entities?.map(renderItem)}
          hideScrollIndicator
          totals={[`${totalID} giao dịch`, helpers.formatNumber(totalMoney, ',') + ' đ', '', '', '']}
          showTotalsInFooter
          // hasZebraStripingOnData
          increasedTableDensity
          // fixedFirstColumns={2}
          // stickyHeader
          footerContent={___('Show page {display_page_number} of {total_record_number} results', {
            display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
            total_record_number: <Text as="strong">{totalItems}</Text>,
          })}
          verticalAlign="middle"
        />
      </>
    ) : (
      emptyData
    );

  const optionForFilter = useMemo(() => {
    let r = [];
    for (let keyofFilter of allowed_transaction_category) {
      r.push({
        label: __('transaction_category_' + keyofFilter),
        value: keyofFilter,
        field: 'transaction_category',
      });
    }
    return r;
  }, []);

  /**
   * TABs
   */

  useEffect(() => {
    let walletUnits = [
      {
        id: 'cash',
        content: __('Ví tiền mặt'),
        description: 'Toàn bộ giao dịch trên ví',
        wallet_unit: 'cash',
      },
    ];
    if (walletTypeList) {
      walletUnits.pop();
      for (let w of walletTypeList) {
        walletUnits.push({
          id: `` + w.id,
          content: w.wallet_name,
          description: w.wallet_description,
          wallet_unit: w.wallet_unit,
        });
      }
    }
    setTabs(walletUnits);
  }, [walletTypeList]);

  useEffect(() => {
    setMainQuery({
      ...initialQuery,
      ...StringQuery,
    });
    if (tabs[walletSelected] && tabs[walletSelected].wallet_unit) getWalletData();
  }, [walletSelected, tabs]);

  return (
    <>
      <Helmet>
        <title>Toàn bộ giao dịch</title>
      </Helmet>
      {entities === null ? (
        <SkeletonLoading fullWidth />
      ) : (
        <Page title="Giao dịch" subtitle="Quản lý toàn bộ giao dịch trên hệ thống liên quan tới ví" compactTitle fullWidth>
          <LegacyCard>
            <Tabs tabs={tabs} selected={walletSelected} onSelect={setWalletSelected}>
              <LegacyCard.Section>
                <div>
                  <Text variant="heading2xl" as="h2">
                    {walletData?.body?.balance === undefined ? '_,___,___ đ' : helpers.formatNumberWithCommas(walletData?.body?.balance || 0)}
                  </Text>
                </div>
              </LegacyCard.Section>
            </Tabs>
          </LegacyCard>

          <br />

          <Card padding="0">
            <SimpleFilter
              loading={loading}
              options={optionForFilter}
              onCallback={filterCallback}
              sortField={[
                {
                  label: 'Số tiền',
                  field: 'amount',
                },
              ]}
            />
            {PagesList}
          </Card>
          <br />
          {totalItems > mainQuery.limit ? (
            <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
          ) : null}
        </Page>
      )}
      <Hotkeys
        keyName="shift+enter"
        onKeyDown={(keyname: any, event: any, _handler) => {
          event.preventDefault();
          setShowModal(true);
        }}
      />

      <AgencyTransactionView show={!!showTransactionDetail} transaction_hash={showTransactionDetail} onClose={() => setShowTransactionDetail('')} />
    </>
  );
}

